<template>
  <div class="photo-collage">
    <div class="editor-container">
      <!-- 左侧：布局模板库 -->
      <div class="layout-sidebar">
        <div class="layout-header">
          <h3>🧩 布局拼接</h3>
        </div>
        
        <!-- 按图片数量筛选 -->
        <div class="layout-filter">
          <button 
            v-for="n in 6" 
            :key="n"
            class="filter-btn"
            :class="{ active: filterCount === n }"
            @click="filterCount = n"
          >
            {{ n }}张
          </button>
          <button 
            class="filter-btn"
            :class="{ active: filterCount === null }"
            @click="filterCount = null"
          >
            全部
          </button>
        </div>

        <!-- 布局模板网格 -->
        <div class="layout-grid">
          <div 
            v-for="layout in filteredLayouts" 
            :key="layout.id"
            class="layout-card"
            :class="{ active: selectedLayout && selectedLayout.id === layout.id }"
            @click="selectLayout(layout)"
          >
            <div class="layout-preview">
              <canvas 
                :id="'previewCanvas_' + layout.id"
                :width="120" 
                :height="80"
                class="preview-canvas"
              ></canvas>
            </div>
            <div class="layout-name">{{ layout.name }}</div>
            <div class="layout-count">{{ layout.cells.length }}张</div>
          </div>
        </div>
      </div>

      <!-- 中间：画布区域 -->
      <div class="canvas-area">
        <div class="batch-upload-bar">
          <button @click="triggerBatchUpload" class="btn-batch-upload">📸 选择多张图片</button>
          <button @click="clearAllImages" class="btn-clear-all" v-if="hasAnyImage">🗑️ 清空所有图片</button>
        </div>

        <div class="canvas-wrapper" :style="canvasWrapperStyle" v-if="selectedLayout">
          <div class="canvas-bg" :style="{ backgroundColor: style.bgColor }"></div>
          
          <div 
            v-for="(cell, index) in selectedLayout.cells" 
            :key="index"
            class="layout-cell"
            :style="getCellPositionStyle(cell, index)"
            :data-index="index"
            :draggable="false"
          >
            <div 
              v-if="cellImages[index]" 
              class="cell-image-wrapper"
              @mousedown="handleMouseDown($event, index)"
              draggable="false"
            >
              <img 
                :src="cellImages[index].src" 
                class="cell-image"
                :style="getImageStyle(index)"
                draggable="false" 
              />
            </div>
            <div v-else class="cell-placeholder" @click="openImageUploadForCell(index)">
              <div class="upload-icon-small">📸</div>
              <div class="upload-text-small">点击上传</div>
            </div>
            <div class="cell-index">{{ index + 1 }}</div>
            <div v-if="cellImages[index]" class="zoom-controls">
                <button @click.stop="zoomImage(index, 'out')" class="zoom-btn zoom-out" title="缩小">−</button>
                <span class="zoom-scale">{{ Math.round((cellScales[index] || 1) * 100) }}%</span>
                <button @click.stop="zoomImage(index, 'in')" class="zoom-btn zoom-in" title="放大">+</button>
                <button @click.stop="resetZoom(index)" class="zoom-btn zoom-reset" title="重置">⟳</button>
            </div>
            <div v-if="cellImages[index]" class="cell-actions">
              <button @click.stop="removeCellImage(index)" class="cell-action-btn" title="删除">🗑️</button>
              <button @click.stop="openImageUploadForCell(index)" class="cell-action-btn" title="替换">🔄</button>
            </div>
          </div>
        </div>
        
        <div v-else class="no-layout-tip">
          ⚠️ 请从左侧选择一个布局模板
        </div>
        
        <input 
          type="file" 
          ref="cellFileInput"
          accept="image/*" 
          style="display: none"
          @change="handleCellImageUpload"
        />
        <input 
          type="file" 
          ref="batchFileInput"
          accept="image/*" 
          multiple
          style="display: none"
          @change="handleBatchImageUpload"
        />
      </div>

      <!-- 右侧：样式设置区 -->
      <div class="settings-sidebar">
        <div class="settings-section">
          <h3>🎨 样式设置</h3>
          
          <div class="setting-group">
            <label>画布比例</label>
            <div class="ratio-buttons">
              <button @click="setCanvasSize(600, 600)" :class="{ active: canvasWidth === 600 && canvasHeight === 600 }">1:1</button>
              <button @click="setCanvasSize(800, 600)" :class="{ active: canvasWidth === 800 && canvasHeight === 600 }">4:3</button>
              <button @click="setCanvasSize(800, 450)" :class="{ active: canvasWidth === 800 && canvasHeight === 450 }">16:9</button>
              <button @click="setCanvasSize(600, 800)" :class="{ active: canvasWidth === 600 && canvasHeight === 800 }">3:4</button>
              <button @click="setCanvasSize(900, 600)" :class="{ active: canvasWidth === 900 && canvasHeight === 600 }">3:2</button>
            </div>
          </div>

          <div class="setting-group">
            <label>边距 (px)</label>
            <input type="range" v-model.number="style.margin" min="0" max="50" @input="updateCellPositions" />
            <span>{{ style.margin }}px</span>
          </div>

          <div class="setting-group">
            <label>圆角 (px)</label>
            <input type="range" v-model.number="style.borderRadius" min="0" max="50" @input="updateCellPositions" />
            <span>{{ style.borderRadius }}px</span>
          </div>

          <div class="setting-group">
            <label>背景颜色</label>
            <input type="color" v-model="style.bgColor" @input="updateCellPositions" />
          </div>
        </div>

        <div class="settings-section" v-if="selectedLayout">
          <h3>📊 当前状态</h3>
          <div class="status-info">
            <div>布局格子数: {{ selectedLayout.cells.length }}</div>
            <div>已上传图片: {{ uploadedCount }} / {{ selectedLayout.cells.length }}</div>
            <div v-if="uploadedCount < selectedLayout.cells.length" class="hint-info">
              💡 点击格子或批量上传均可添加图片
            </div>
            <div v-else class="success-info">
              ✅ 所有格子都已上传，可以导出啦！
            </div>
          </div>
        </div>

        <div class="export-controls">
          <h3>🖼️ 导出拼图</h3>
          <div class="tab-group">
            <div class="tab-header">
              <button 
                v-for="format in formatOptions" 
                :key="format.value"
                class="tab-btn"
                :class="{ active: exportFormat === format.value }"
                @click="exportFormat = format.value"
              >
                {{ format.label }}
              </button>
            </div>
          </div>
          <!-- <div class="tab-group">
            <div class="tab-header">
              <button 
                v-for="size in sizeOptions" 
                :key="size.value"
                class="tab-btn"
                :class="{ active: exportScale === size.value }"
                @click="exportScale = size.value"
              >
                {{ size.label }}
              </button>
            </div>
          </div> -->
          <div class="tab-group">
            <div class="tab-header">
              <span>质量：</span>
              <button 
                v-for="quality in qualityOptions" 
                :key="quality.value"
                class="tab-btn"
                :class="{ active: exportQuality === quality.value }"
                @click="exportQuality = quality.value"
              >
                {{ quality.label }}
              </button>
            </div>
          </div>
          <div class="export-controls-buttons">
            <button @click="exportImage" class="btn-export" :disabled="!hasAnyImage">
              💾 下载拼图
            </button>
            <button @click="useCollage" class="btn-use" :disabled="!hasAnyImage">
              ⬆️ 上传拼图
            </button>
          </div>
        </div>
      </div>
    </div>
    
    <!-- 拖拽预览图片 -->
    <div 
      v-if="dragPreviewVisible" 
      class="drag-preview"
      :style="{
        left: dragPreviewX + 'px',
        top: dragPreviewY + 'px',
        width: dragPreviewWidth + 'px',
        height: dragPreviewHeight + 'px',
        backgroundImage: 'url(' + dragPreviewImage + ')',
        backgroundSize: 'contain',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        position: 'fixed',
        zIndex: 9999,
        pointerEvents: 'none',
        borderRadius: '8px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
        opacity: 0.8,
        backgroundColor: '#f0f0f0'
      }"
    ></div>
  </div>
</template>

<script>
export default {
  name: 'PhotoCollageModal',
  data() {
    return {
      filterCount: null,
      selectedLayout: null,
      cellImages: [],
      cellScales: [],
      imageOffsets: [],
      currentUploadCellIndex: null,
      
      canvasWidth: 800,
      canvasHeight: 600,
      // exportScale: 0.96,
      exportScale: 1.92,  // 默认高清 1536
      exportFormat: 'image/jpeg',
      exportQuality: 0.9,
      formatOptions: [
        { label: 'PNG', value: 'image/png' },
        { label: 'JPG', value: 'image/jpeg' }
      ],
      sizeOptions: [
        { label: '普通\n768', value: 0.96 },
        { label: '标准\n1080', value: 1.35 },
        { label: '高清\n1536', value: 1.92 },
        { label: '超清\n2160', value: 2.7 }
      ],
      qualityOptions: [
        { label: '100%', value: 1 },
        { label: '90%', value: 0.9 },
        { label: '80%', value: 0.8 }
      ],

      style: {
        margin: 10,
        borderRadius: 8,
        bgColor: '#ffffff',
      },
      
      cellPositions: [],
      
      layoutTemplates: [
        { id: 's1', name: '全屏', cells: [{ w: 100, h: 100, x: 0, y: 0 }] },
        { id: 's2', name: '居中', cells: [{ w: 80, h: 80, x: 10, y: 10 }] },
        { id: 'd1', name: '左右', cells: [{ w: 50, h: 100, x: 0, y: 0 }, { w: 50, h: 100, x: 50, y: 0 }] },
        { id: 'd2', name: '上下', cells: [{ w: 100, h: 50, x: 0, y: 0 }, { w: 100, h: 50, x: 0, y: 50 }] },
        { id: 'd3', name: '左大右小', cells: [{ w: 65, h: 100, x: 0, y: 0 }, { w: 35, h: 100, x: 65, y: 0 }] },
        { id: 'd4', name: '左小右大', cells: [{ w: 35, h: 100, x: 0, y: 0 }, { w: 65, h: 100, x: 35, y: 0 }] },
        { id: 'd5', name: '上大下小', cells: [{ w: 100, h: 65, x: 0, y: 0 }, { w: 100, h: 35, x: 0, y: 65 }] },
        { id: 'd6', name: '上小下大', cells: [{ w: 100, h: 35, x: 0, y: 0 }, { w: 100, h: 65, x: 0, y: 35 }] },
        { id: 't1', name: '三列', cells: [{ w: 33.33, h: 100, x: 0, y: 0 }, { w: 33.33, h: 100, x: 33.33, y: 0 }, { w: 33.33, h: 100, x: 66.66, y: 0 }] },
        { id: 't2', name: '三行', cells: [{ w: 100, h: 33.33, x: 0, y: 0 }, { w: 100, h: 33.33, x: 0, y: 33.33 }, { w: 100, h: 33.33, x: 0, y: 66.66 }] },
        { id: 't3', name: '左大右双', cells: [{ w: 50, h: 100, x: 0, y: 0 }, { w: 50, h: 50, x: 50, y: 0 }, { w: 50, h: 50, x: 50, y: 50 }] },
        { id: 't4', name: '左双右大', cells: [{ w: 50, h: 50, x: 0, y: 0 }, { w: 50, h: 50, x: 0, y: 50 }, { w: 50, h: 100, x: 50, y: 0 }] },
        { id: 't5', name: '上大下双', cells: [{ w: 100, h: 50, x: 0, y: 0 }, { w: 50, h: 50, x: 0, y: 50 }, { w: 50, h: 50, x: 50, y: 50 }] },
        { id: 't6', name: '上双下大', cells: [{ w: 50, h: 50, x: 0, y: 0 }, { w: 50, h: 50, x: 50, y: 0 }, { w: 100, h: 50, x: 0, y: 50 }] },
        { id: 'col4', name: '四列', cells: [{ w: 25, h: 100, x: 0, y: 0 },{ w: 25, h: 100, x: 25, y: 0 },{ w: 25, h: 100, x: 50, y: 0 },{ w: 25, h: 100, x: 75, y: 0 }]},
        { id: 'row4', name: '四行', cells: [{ w: 100, h: 25, x: 0, y: 0 },{ w: 100, h: 25, x: 0, y: 25 },{ w: 100, h: 25, x: 0, y: 50 },{ w: 100, h: 25, x: 0, y: 75 }]},
        { id: 'q1', name: '2x2网格', cells: [{ w: 50, h: 50, x: 0, y: 0 }, { w: 50, h: 50, x: 50, y: 0 }, { w: 50, h: 50, x: 0, y: 50 }, { w: 50, h: 50, x: 50, y: 50 }] },
        { id: 'q2', name: '左大右三', cells: [{ w: 50, h: 100, x: 0, y: 0 }, { w: 50, h: 33.33, x: 50, y: 0 }, { w: 50, h: 33.33, x: 50, y: 33.33 }, { w: 50, h: 33.33, x: 50, y: 66.66 }] },
        { id: 'q3', name: '左三右大', cells: [{ w: 50, h: 33.33, x: 0, y: 0 }, { w: 50, h: 33.33, x: 0, y: 33.33 }, { w: 50, h: 33.33, x: 0, y: 66.66 }, { w: 50, h: 100, x: 50, y: 0 }] },
        { id: 'q4', name: '上大下三', cells: [{ w: 100, h: 50, x: 0, y: 0 }, { w: 33.33, h: 50, x: 0, y: 50 }, { w: 33.33, h: 50, x: 33.33, y: 50 }, { w: 33.33, h: 50, x: 66.66, y: 50 }] },
        { id: 'q5', name: '上三下大', cells: [{ w: 33.33, h: 50, x: 0, y: 0 }, { w: 33.33, h: 50, x: 33.33, y: 0 }, { w: 33.33, h: 50, x: 66.66, y: 0 }, { w: 100, h: 50, x: 0, y: 50 }] },
        { id: 'col5', name: '五列', cells: [{ w: 20, h: 100, x: 0, y: 0 },{ w: 20, h: 100, x: 20, y: 0 },{ w: 20, h: 100, x: 40, y: 0 },{ w: 20, h: 100, x: 60, y: 0 },{ w: 20, h: 100, x: 80, y: 0 }]},
        { id: 'row5', name: '五行', cells: [{ w: 100, h: 20, x: 0, y: 0 },{ w: 100, h: 20, x: 0, y: 20 },{ w: 100, h: 20, x: 0, y: 40 },{ w: 100, h: 20, x: 0, y: 60 },{ w: 100, h: 20, x: 0, y: 80 }]},
        { id: 'f1', name: '2x3缺一', cells: [{ w: 33.33, h: 50, x: 0, y: 0 }, { w: 33.33, h: 50, x: 33.33, y: 0 }, { w: 33.33, h: 50, x: 66.66, y: 0 }, { w: 50, h: 50, x: 0, y: 50 }, { w: 50, h: 50, x: 50, y: 50 }] },
        { id: 'f2', name: '左大右四', cells: [{ w: 40, h: 100, x: 0, y: 0 }, { w: 60, h: 25, x: 40, y: 0 }, { w: 60, h: 25, x: 40, y: 25 }, { w: 60, h: 25, x: 40, y: 50 }, { w: 60, h: 25, x: 40, y: 75 }] },
        { id: 'f3', name: '左四右大', cells: [{ w: 60, h: 25, x: 0, y: 0 }, { w: 60, h: 25, x: 0, y: 25 }, { w: 60, h: 25, x: 0, y: 50 }, { w: 60, h: 25, x: 0, y: 75 }, { w: 40, h: 100, x: 60, y: 0 }] },
        { id: 'col6', name: '六列', cells: [{ w: 16.66, h: 100, x: 0, y: 0 },{ w: 16.66, h: 100, x: 16.66, y: 0 },{ w: 16.66, h: 100, x: 33.33, y: 0 },{ w: 16.66, h: 100, x: 50, y: 0 },{ w: 16.66, h: 100, x: 66.66, y: 0 },{ w: 16.66, h: 100, x: 83.33, y: 0 }]},
        { id: 'row6', name: '六行', cells: [{ w: 100, h: 16.66, x: 0, y: 0 },{ w: 100, h: 16.66, x: 0, y: 16.66 },{ w: 100, h: 16.66, x: 0, y: 33.33 },{ w: 100, h: 16.66, x: 0, y: 50 },{ w: 100, h: 16.66, x: 0, y: 66.66 },{ w: 100, h: 16.66, x: 0, y: 83.33 }]},
        { id: 'x1', name: '2x3网格', cells: [{ w: 50, h: 33.33, x: 0, y: 0 }, { w: 50, h: 33.33, x: 50, y: 0 }, { w: 50, h: 33.33, x: 0, y: 33.33 }, { w: 50, h: 33.33, x: 50, y: 33.33 }, { w: 50, h: 33.33, x: 0, y: 66.66 }, { w: 50, h: 33.33, x: 50, y: 66.66 }] },
        { id: 'x2', name: '3x2网格', cells: [{ w: 33.33, h: 50, x: 0, y: 0 }, { w: 33.33, h: 50, x: 33.33, y: 0 }, { w: 33.33, h: 50, x: 66.66, y: 0 }, { w: 33.33, h: 50, x: 0, y: 50 }, { w: 33.33, h: 50, x: 33.33, y: 50 }, { w: 33.33, h: 50, x: 66.66, y: 50 }] },
        { id: 'x3', name: '左大右五', cells: [{ w: 40, h: 100, x: 0, y: 0 }, { w: 60, h: 20, x: 40, y: 0 }, { w: 60, h: 20, x: 40, y: 20 }, { w: 60, h: 20, x: 40, y: 40 }, { w: 60, h: 20, x: 40, y: 60 }, { w: 60, h: 20, x: 40, y: 80 }] },
        { id: 'x4', name: '左五右大', cells: [{ w: 60, h: 20, x: 0, y: 0 }, { w: 60, h: 20, x: 0, y: 20 }, { w: 60, h: 20, x: 0, y: 40 }, { w: 60, h: 20, x: 0, y: 60 }, { w: 60, h: 20, x: 0, y: 80 }, { w: 40, h: 100, x: 60, y: 0 }] },
      ],

      dragImageIndex: null,
      draggingImage: false,
      dragStartPos: { x: 0, y: 0 },
      dragStartOffset: { x: 0, y: 0 },
      dragTargetCell: null,
      
      scaleStep: 0.1,
      minScale: 0.5,
      maxScale: 3,
      
      dragPreviewVisible: false,
      dragPreviewX: 0,
      dragPreviewY: 0,
      dragPreviewWidth: 120,
      dragPreviewHeight: 120,
      dragPreviewImage: '',
      
      isDraggingOut: false,
    };
  },
  
  computed: {
    filteredLayouts: function() {
      if (this.filterCount === null) return this.layoutTemplates;
      return this.layoutTemplates.filter(function(l) {
        return l.cells.length === this.filterCount;
      }.bind(this));
    },
    
    hasAnyImage: function() {
      if (!this.cellImages) return false;
      for (var i = 0; i < this.cellImages.length; i++) {
        if (this.cellImages[i]) return true;
      }
      return false;
    },
    
    uploadedCount: function() {
      if (!this.cellImages) return 0;
      var count = 0;
      for (var i = 0; i < this.cellImages.length; i++) {
        if (this.cellImages[i]) count++;
      }
      return count;
    },
    
    canvasWrapperStyle: function() {
      return {
        width: this.canvasWidth + 'px',
        height: this.canvasHeight + 'px',
        position: 'relative',
        borderRadius: this.style.borderRadius + 'px',
        overflow: 'hidden',
        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
      };
    },
  },
  
  watch: {
    selectedLayout: function(newLayout, oldLayout) {
      if (newLayout) {
        this.transferImagesToNewLayout(oldLayout, newLayout);
      }
    },
  },
  
  mounted: function() {
    this.selectedLayout = this.layoutTemplates[0];
    this.imageOffsets = [];
    this.$nextTick(function() {
      this.drawAllPreviews();
      this.updateCellPositions();
    });
  },
  
  updated: function() {
    this.$nextTick(function() {
      this.drawAllPreviews();
    });
  },
  
  methods: {
    drawLayoutPreview: function(layout, canvasId) {
      var canvas = document.getElementById(canvasId);
      if (!canvas) return;
      
      var ctx = canvas.getContext('2d');
      var width = canvas.width;
      var height = canvas.height;
      
      ctx.clearRect(0, 0, width, height);
      ctx.fillStyle = '#f0f0f0';
      ctx.fillRect(0, 0, width, height);
      
      for (var i = 0; i < layout.cells.length; i++) {
        var cell = layout.cells[i];
        var x = (cell.x / 100) * width;
        var y = (cell.y / 100) * height;
        var w = (cell.w / 100) * width;
        var h = (cell.h / 100) * height;
        
        ctx.fillStyle = '#007DFF';
        ctx.fillRect(x, y, w, h);
        
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 1;
        ctx.strokeRect(x, y, w, h);
        
        ctx.fillStyle = '#ffffff';
        ctx.font = 'bold 12px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(String(i + 1), x + w / 2, y + h / 2);
      }
    },
    
    drawAllPreviews: function() {
      for (var i = 0; i < this.layoutTemplates.length; i++) {
        var layout = this.layoutTemplates[i];
        this.drawLayoutPreview(layout, 'previewCanvas_' + layout.id);
      }
    },
    
    selectLayout: function(layout) {
      this.selectedLayout = layout;
    },
    
    transferImagesToNewLayout: function(oldLayout, newLayout) {
      if (!newLayout) return;
      
      var oldImages = this.cellImages || [];
      var oldScales = this.cellScales || [];
      var oldOffsets = this.imageOffsets || [];
      
      var newCellImages = new Array(newLayout.cells.length);
      var newCellScales = new Array(newLayout.cells.length);
      var newImageOffsets = new Array(newLayout.cells.length);
      
      for (var i = 0; i < newLayout.cells.length; i++) {
        newCellImages[i] = null;
        newCellScales[i] = 1;
        newImageOffsets[i] = { x: 0, y: 0 };
      }
      
      for (var j = 0; j < oldImages.length && j < newLayout.cells.length; j++) {
        if (oldImages[j]) {
          newCellImages[j] = oldImages[j];
          newCellScales[j] = oldScales[j] || 1;
          newImageOffsets[j] = oldOffsets[j] || { x: 0, y: 0 };
        }
      }
      
      this.cellImages = newCellImages;
      this.cellScales = newCellScales;
      this.imageOffsets = newImageOffsets;
      this.updateCellPositions();
    },
    
    updateCellPositions: function() {
      if (!this.selectedLayout) return;
      
      var margin = this.style.margin;
      var canvasW = this.canvasWidth;
      var canvasH = this.canvasHeight;
      var availableW = canvasW - margin * 2;
      var availableH = canvasH - margin * 2;
      var positions = [];
      
      for (var i = 0; i < this.selectedLayout.cells.length; i++) {
        var cell = this.selectedLayout.cells[i];
        var width = (cell.w / 100) * availableW;
        var height = (cell.h / 100) * availableH;
        var left = margin + (cell.x / 100) * availableW;
        var top = margin + (cell.y / 100) * availableH;
        
        positions.push({
          left: left + 'px',
          top: top + 'px',
          width: width + 'px',
          height: height + 'px',
        });
      }
      this.cellPositions = positions;
    },
    
    getCellPositionStyle: function(cell, index) {
      if (this.cellPositions[index]) {
        return {
          position: 'absolute',
          left: this.cellPositions[index].left,
          top: this.cellPositions[index].top,
          width: this.cellPositions[index].width,
          height: this.cellPositions[index].height,
          borderRadius: this.style.borderRadius + 'px',
          overflow: 'hidden',
          cursor: 'default',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        };
      }
      return { display: 'none' };
    },
    
    getCellRect: function(index) {
      var cellElement = this.$el.querySelector('.layout-cell[data-index="' + index + '"]');
      if (!cellElement) return null;
      var rect = cellElement.getBoundingClientRect();
      return {
        width: rect.width,
        height: rect.height,
      };
    },

    getImageNaturalSize: function(index) {
      var imgData = this.cellImages[index];
      if (!imgData) return null;
      return {
        width: imgData.width,
        height: imgData.height,
      };
    },

    // 计算图片实际显示需要的偏移和缩放
    getImageTransform: function(index) {
      var scale = this.cellScales[index] || 1;
      var offset = this.imageOffsets[index] || { x: 0, y: 0 };
      
      var naturalSize = this.getImageNaturalSize(index);
      var cellRect = this.getCellRect(index);
      
      if (!naturalSize || !cellRect) {
        return {
          scale: 1,
          translateX: 0,
          translateY: 0,
        };
      }
      
      // 计算图片填满容器所需的基础缩放
      var baseScaleX = cellRect.width / naturalSize.width;
      var baseScaleY = cellRect.height / naturalSize.height;
      var baseScale = Math.max(baseScaleX, baseScaleY);
      
      // 最终缩放 = 基础缩放 * 用户缩放
      var finalScale = baseScale * scale;
      
      // 计算偏移（注意：偏移是在用户缩放空间中的，需要转换）
      var translateX = offset.x * baseScale;
      var translateY = offset.y * baseScale;
      
      return {
        scale: finalScale,
        translateX: translateX,
        translateY: translateY,
      };
    },
    
    getImageStyle: function(index) {
      var transform = this.getImageTransform(index);
      
      return {
        width: 'auto',
        height: 'auto',
        maxWidth: 'none',
        maxHeight: 'none',
        transform: `translate(${transform.translateX}px, ${transform.translateY}px) scale(${transform.scale})`,
        transformOrigin: 'center center',
        transition: this.draggingImage && this.dragImageIndex === index ? 'none' : 'transform 0.2s ease',
        pointerEvents: 'none',
        display: 'block',
        position: 'relative',
      };
    },
    
    openImageUploadForCell: function(cellIndex) {
      this.currentUploadCellIndex = cellIndex;
      this.$refs.cellFileInput.click();
    },
    
    // 计算可移动的最大范围
    getMaxOffset: function(index) {
      var scale = this.cellScales[index] || 1;
      var naturalSize = this.getImageNaturalSize(index);
      var cellRect = this.getCellRect(index);
      
      if (!naturalSize || !cellRect) return { maxX: 0, maxY: 0 };
      
      // 基础缩放
      var baseScaleX = cellRect.width / naturalSize.width;
      var baseScaleY = cellRect.height / naturalSize.height;
      var baseScale = Math.max(baseScaleX, baseScaleY);
      
      // 最终缩放后的图片尺寸
      var finalWidth = naturalSize.width * baseScale * scale;
      var finalHeight = naturalSize.height * baseScale * scale;
      
      // 可移动的最大距离（在变换后的坐标系中）
      var maxX = Math.max(0, (finalWidth - cellRect.width) / 2);
      var maxY = Math.max(0, (finalHeight - cellRect.height) / 2);
      
      // 转换回用户偏移空间
      return {
        maxX: maxX / baseScale,
        maxY: maxY / baseScale,
      };
    },
    
    zoomImage: function(index, direction) {
      var currentScale = this.cellScales[index] || 1;
      var newScale;
      
      if (direction === 'in') {
        newScale = Math.min(currentScale + this.scaleStep, this.maxScale);
      } else {
        newScale = Math.max(currentScale - this.scaleStep, this.minScale);
      }
      
      if (newScale === currentScale) return;
      
      // 获取当前变换信息，计算中心点保持
      var currentTransform = this.getImageTransform(index);
      var cellRect = this.getCellRect(index);
      var naturalSize = this.getImageNaturalSize(index);
      
      if (!cellRect || !naturalSize) {
        this.$set(this.cellScales, index, parseFloat(newScale.toFixed(2)));
        return;
      }
      
      // 基础缩放
      var baseScaleX = cellRect.width / naturalSize.width;
      var baseScaleY = cellRect.height / naturalSize.height;
      var baseScale = Math.max(baseScaleX, baseScaleY);
      
      // 当前最终缩放
      var currentFinalScale = baseScale * currentScale;
      // 新最终缩放
      var newFinalScale = baseScale * newScale;
      
      // 计算当前图片显示区域相对于容器的偏移比例
      var currentOffsetX = this.imageOffsets[index]?.x || 0;
      var currentOffsetY = this.imageOffsets[index]?.y || 0;
      
      // 当前图片实际显示尺寸
      var currentDisplayWidth = naturalSize.width * currentFinalScale;
      var currentDisplayHeight = naturalSize.height * currentFinalScale;
      
      // 当前可视区域中心点在图片中的比例位置
      var centerXRatio = 0.5;
      var centerYRatio = 0.5;
      
      if (currentDisplayWidth > cellRect.width) {
        var currentTranslateX = currentOffsetX * baseScale;
        centerXRatio = (cellRect.width / 2 - currentTranslateX) / currentDisplayWidth;
        centerXRatio = Math.max(0, Math.min(1, centerXRatio));
      }
      
      if (currentDisplayHeight > cellRect.height) {
        var currentTranslateY = currentOffsetY * baseScale;
        centerYRatio = (cellRect.height / 2 - currentTranslateY) / currentDisplayHeight;
        centerYRatio = Math.max(0, Math.min(1, centerYRatio));
      }
      
      // 更新缩放
      this.$set(this.cellScales, index, parseFloat(newScale.toFixed(2)));
      
      // 计算新偏移，保持中心点相同
      this.$nextTick(() => {
        var newDisplayWidth = naturalSize.width * newFinalScale;
        var newDisplayHeight = naturalSize.height * newFinalScale;
        
        var newTranslateX = cellRect.width / 2 - centerXRatio * newDisplayWidth;
        var newTranslateY = cellRect.height / 2 - centerYRatio * newDisplayHeight;
        
        // 转换回用户偏移空间
        var newOffsetX = newTranslateX / baseScale;
        var newOffsetY = newTranslateY / baseScale;
        
        var maxMove = this.getMaxOffset(index);
        newOffsetX = Math.max(-maxMove.maxX, Math.min(maxMove.maxX, newOffsetX));
        newOffsetY = Math.max(-maxMove.maxY, Math.min(maxMove.maxY, newOffsetY));
        
        var newOffsets = [...this.imageOffsets];
        newOffsets[index] = { x: newOffsetX, y: newOffsetY };
        this.imageOffsets = newOffsets;
      });
    },
    
    resetZoom: function(index) {
      this.$set(this.cellScales, index, 1);
      var newOffsets = [...this.imageOffsets];
      newOffsets[index] = { x: 0, y: 0 };
      this.imageOffsets = newOffsets;
    },
    
    handleCellImageUpload: function(event) {
      var self = this;
      var file = event.target.files[0];
      if (!file) return;
      
      var reader = new FileReader();
      reader.onload = function(e) {
        var img = new Image();
        img.onload = function() {
          var newCellImages = self.cellImages.slice();
          newCellImages[self.currentUploadCellIndex] = {
            src: e.target.result,
            file: file,
            width: img.width,
            height: img.height
          };
          self.cellImages = newCellImages;
          self.$set(self.cellScales, self.currentUploadCellIndex, 1);
          var newOffsets = [...self.imageOffsets];
          newOffsets[self.currentUploadCellIndex] = { x: 0, y: 0 };
          self.imageOffsets = newOffsets;
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
      event.target.value = '';
    },
    
    handleMouseDown: function(event, index) {
      if (!this.cellImages[index]) return;
      
      event.preventDefault();
      event.stopPropagation();
      
      this.draggingImage = true;
      this.dragImageIndex = index;
      this.dragStartPos = {
        x: event.clientX,
        y: event.clientY
      };
      
      var currentOffset = this.imageOffsets[index] || { x: 0, y: 0 };
      this.dragStartOffset = { x: currentOffset.x, y: currentOffset.y };
      
      this.dragTargetCell = null;
      this.isDraggingOut = false;
      
      window.addEventListener('mousemove', this.onMouseMove);
      window.addEventListener('mouseup', this.onMouseUp);
      
      event.target.style.cursor = 'grabbing';
    },
    
    updateDragPreviewPosition: function(mouseX, mouseY) {
      this.dragPreviewX = mouseX - this.dragPreviewWidth / 2;
      this.dragPreviewY = mouseY - this.dragPreviewHeight / 2;
    },
    
    onMouseMove: function(event) {
      if (!this.draggingImage) return;
      
      event.preventDefault();
      
      var targetIndex = this.findCellUnderCursor(event.clientX, event.clientY);
      
      if (targetIndex !== null && targetIndex !== this.dragImageIndex) {
        if (!this.dragPreviewVisible) {
          this.dragPreviewWidth = 120;
          this.dragPreviewHeight = 120;
          this.dragPreviewImage = this.cellImages[this.dragImageIndex].src;
          this.dragPreviewVisible = true;
        }
        this.updateDragPreviewPosition(event.clientX, event.clientY);
        
        if (this.dragTargetCell !== targetIndex) {
          this.dragTargetCell = targetIndex;
          this.highlightCell(targetIndex, true);
        }
        this.clearOtherHighlights(targetIndex);
        this.isDraggingOut = false;
      } else if (targetIndex === this.dragImageIndex) {
        if (this.dragPreviewVisible) {
          this.dragPreviewVisible = false;
        }
        this.dragTargetCell = null;
        this.moveImage(event);
        this.clearAllHighlights();
        this.isDraggingOut = false;
      } else {
        if (!this.dragPreviewVisible && !this.isDraggingOut) {
          this.dragPreviewWidth = 120;
          this.dragPreviewHeight = 120;
          this.dragPreviewImage = this.cellImages[this.dragImageIndex].src;
          this.dragPreviewVisible = true;
          this.isDraggingOut = true;
        }
        if (this.dragPreviewVisible) {
          this.updateDragPreviewPosition(event.clientX, event.clientY);
        }
        this.dragTargetCell = null;
        this.clearAllHighlights();
      }
    },
    
    moveImage: function(event) {
      var deltaX = event.clientX - this.dragStartPos.x;
      var deltaY = event.clientY - this.dragStartPos.y;
      
      var naturalSize = this.getImageNaturalSize(this.dragImageIndex);
      var cellRect = this.getCellRect(this.dragImageIndex);
      
      if (!naturalSize || !cellRect) return;
      
      // 基础缩放
      var baseScaleX = cellRect.width / naturalSize.width;
      var baseScaleY = cellRect.height / naturalSize.height;
      var baseScale = Math.max(baseScaleX, baseScaleY);
      
      // 将鼠标移动距离转换为用户偏移空间的变化
      var deltaOffsetX = deltaX / baseScale;
      var deltaOffsetY = deltaY / baseScale;
      
      var newX = this.dragStartOffset.x + deltaOffsetX;
      var newY = this.dragStartOffset.y + deltaOffsetY;
      
      var maxMove = this.getMaxOffset(this.dragImageIndex);
      
      newX = Math.max(-maxMove.maxX, Math.min(maxMove.maxX, newX));
      newY = Math.max(-maxMove.maxY, Math.min(maxMove.maxY, newY));
      
      var newOffsets = [...this.imageOffsets];
      newOffsets[this.dragImageIndex] = { x: newX, y: newY };
      this.imageOffsets = newOffsets;
    },
    
    onMouseUp: function(event) {
      if (!this.draggingImage) return;
      
      this.dragPreviewVisible = false;
      
      if (this.dragTargetCell !== null && this.dragTargetCell !== this.dragImageIndex) {
        this.swapImages(this.dragImageIndex, this.dragTargetCell);
      }
      
      this.draggingImage = false;
      this.dragImageIndex = null;
      this.dragTargetCell = null;
      this.isDraggingOut = false;
      
      this.clearAllHighlights();
      
      window.removeEventListener('mousemove', this.onMouseMove);
      window.removeEventListener('mouseup', this.onMouseUp);
      
      if (event && event.target) {
        event.target.style.cursor = '';
      }
    },
    
    findCellUnderCursor: function(clientX, clientY) {
      var canvasWrapper = this.$el.querySelector('.canvas-wrapper');
      if (!canvasWrapper) return null;
      
      var rect = canvasWrapper.getBoundingClientRect();
      var relativeX = clientX - rect.left;
      var relativeY = clientY - rect.top;
      
      for (var i = 0; i < this.selectedLayout.cells.length; i++) {
        var position = this.cellPositions[i];
        if (position) {
          var left = parseFloat(position.left);
          var top = parseFloat(position.top);
          var width = parseFloat(position.width);
          var height = parseFloat(position.height);
          
          if (relativeX >= left && relativeX <= left + width &&
              relativeY >= top && relativeY <= top + height) {
            return i;
          }
        }
      }
      return null;
    },
    
    highlightCell: function(index, highlight) {
      var cells = this.$el.querySelectorAll('.layout-cell');
      if (cells[index]) {
        if (highlight) {
          cells[index].style.borderColor = '#007DFF';
          cells[index].style.borderWidth = '3px';
          cells[index].style.backgroundColor = 'rgba(0, 125, 255, 0.1)';
        } else {
          cells[index].style.borderColor = '';
          cells[index].style.borderWidth = '';
          cells[index].style.backgroundColor = '';
        }
      }
    },
    
    clearOtherHighlights: function(keepIndex) {
      var cells = this.$el.querySelectorAll('.layout-cell');
      cells.forEach(function(cell, idx) {
        if (idx !== keepIndex) {
          cell.style.borderColor = '';
          cell.style.borderWidth = '';
          cell.style.backgroundColor = '';
        }
      });
    },
    
    clearAllHighlights: function() {
      var cells = this.$el.querySelectorAll('.layout-cell');
      cells.forEach(function(cell) {
        cell.style.borderColor = '';
        cell.style.borderWidth = '';
        cell.style.backgroundColor = '';
      });
    },
    
    swapImages: function(sourceIndex, targetIndex) {
      var newCellImages = [...this.cellImages];
      var newCellScales = [...this.cellScales];
      var newImageOffsets = [...this.imageOffsets];
      
      var tempImage = newCellImages[sourceIndex];
      var tempScale = newCellScales[sourceIndex];
      var tempOffset = newImageOffsets[sourceIndex];
      
      newCellImages[sourceIndex] = newCellImages[targetIndex];
      newCellScales[sourceIndex] = newCellScales[targetIndex];
      newImageOffsets[sourceIndex] = newImageOffsets[targetIndex];
      
      newCellImages[targetIndex] = tempImage;
      newCellScales[targetIndex] = tempScale;
      newImageOffsets[targetIndex] = tempOffset;
      
      this.cellImages = newCellImages;
      this.cellScales = newCellScales;
      this.imageOffsets = newImageOffsets;
    },
    
    triggerBatchUpload: function() {
      this.$refs.batchFileInput.click();
    },
    
    handleBatchImageUpload: function(event) {
      var files = Array.from(event.target.files);
      if (!files.length) return;
      
      var count = files.length;
      
      // 优先：用户已选模板，且数量刚好匹配 → 直接用当前模板
      if (this.selectedLayout && this.selectedLayout.cells.length === count) {
        this.fillImagesToCurrentLayout(files);
      } else {
        // 否则：按数量找第一个匹配的模板
        var matchedLayout = this.layoutTemplates.find(function(layout) {
          return layout.cells.length === count;
        });
        
        if (matchedLayout) {
          this.selectedLayout = matchedLayout;
          this.$nextTick(function() {
            this.fillImagesToCurrentLayout(files);
          });
        } else {
          alert(`没有恰好容纳 ${count} 张图片的布局模板，请手动选择布局后再上传。`);
        }
      }
      
      event.target.value = '';
    },
    
    fillImagesToCurrentLayout: function(files) {
      if (!this.selectedLayout) return;
      var cellCount = this.selectedLayout.cells.length;
      var newCellImages = [];
      var newCellScales = [];
      var newImageOffsets = [];
      
      var fillLimit = Math.min(files.length, cellCount);
      var self = this;
      var loadedCount = 0;
      
      for (var i = 0; i < fillLimit; i++) {
        newCellImages[i] = null;
        newCellScales[i] = 1;
        newImageOffsets[i] = { x: 0, y: 0 };
      }
      
      for (var i = 0; i < fillLimit; i++) {
        var file = files[i];
        var reader = new FileReader();
        reader.onload = (function(idx) {
          return function(e) {
            var img = new Image();
            img.onload = function() {
              newCellImages[idx] = {
                src: e.target.result,
                file: files[idx],
                width: img.width,
                height: img.height
              };
              loadedCount++;
              
              if (loadedCount === fillLimit) {
                self.cellImages = newCellImages;
                self.cellScales = newCellScales;
                self.imageOffsets = newImageOffsets;
              }
            };
            img.src = e.target.result;
          };
        })(i);
        reader.readAsDataURL(file);
      }
      
      for (var i = fillLimit; i < cellCount; i++) {
        newCellImages[i] = null;
        newCellScales[i] = 1;
        newImageOffsets[i] = { x: 0, y: 0 };
      }
    },
    
    removeCellImage: function(cellIndex) {
      var newCellImages = this.cellImages.slice();
      newCellImages[cellIndex] = null;
      this.cellImages = newCellImages;
      this.$set(this.cellScales, cellIndex, 1);
      var newOffsets = [...this.imageOffsets];
      newOffsets[cellIndex] = { x: 0, y: 0 };
      this.imageOffsets = newOffsets;
    },
    
    clearAllImages: function() {
      if (confirm('确定要清空所有图片吗？')) {
        var newCellImages = new Array(this.selectedLayout.cells.length);
        var newCellScales = new Array(this.selectedLayout.cells.length);
        var newImageOffsets = new Array(this.selectedLayout.cells.length);
        for (var i = 0; i < newCellImages.length; i++) {
          newCellImages[i] = null;
          newCellScales[i] = 1;
          newImageOffsets[i] = { x: 0, y: 0 };
        }
        this.cellImages = newCellImages;
        this.cellScales = newCellScales;
        this.imageOffsets = newImageOffsets;
      }
    },
    
    setCanvasSize: function(width, height) {
      this.canvasWidth = width;
      this.canvasHeight = height;
      this.$nextTick(function() {
        this.updateCellPositions();
      });
    },
    
    exportImage: async function() {
      var self = this;
      var result = await self.generateCollageImage();
      if (result) {
        var link = document.createElement('a');
        var extension = self.exportFormat === 'image/jpeg' ? 'jpg' : 'png';
        link.download = 'collage-' + Date.now() + '.' + extension;
        link.href = result;
        link.click();
      }
    },
    
    useCollage: async function() {
      var self = this;
      var result = await self.generateCollageImage();
      if (result) {
        this.$emit('use-collage', result);
        this.$emit('close');
      }
    },
    
    generateCollageImage: async function() {
      var self = this;
      
     // 根据实际图片像素，算一个刚好够用的导出尺寸
      var maxW = 0, maxH = 0;
      for (var i = 0; i < self.selectedLayout.cells.length; i++) {
        var d = self.cellImages[i];
        if (!d) continue;
        var c = self.selectedLayout.cells[i];
        maxW = Math.max(maxW, d.width  / (c.w / 100));
        maxH = Math.max(maxH, d.height / (c.h / 100));
      }
      var ratio = self.canvasWidth / self.canvasHeight;
      var exportHeight = Math.min(Math.round(maxH), Math.round(maxW / ratio), 2160);
      var exportWidth  = Math.round(exportHeight * ratio);
      
      var canvas = document.createElement('canvas');
      canvas.width = exportWidth;
      canvas.height = exportHeight;
      var ctx = canvas.getContext('2d');
      
      ctx.fillStyle = self.style.bgColor;
      ctx.fillRect(0, 0, exportWidth, exportHeight);
      
      // 计算整体缩放比例（从显示画布到导出画布）
      var globalScale = exportHeight / self.canvasHeight;
      
      for (var i = 0; i < self.selectedLayout.cells.length; i++) {
        var cellImage = self.cellImages[i];
        if (!cellImage) continue;
        
        var cell = self.selectedLayout.cells[i];
        var imageScale = self.cellScales[i] || 1;
        var offset = this.imageOffsets[i] || { x: 0, y: 0 };
        
        var img = new Image();
        img.src = cellImage.src;
        
        await new Promise((resolve) => {
          img.onload = resolve;
        });
        
        var imgWidth = img.width;
        var imgHeight = img.height;
        
        // 计算格子位置和尺寸（在导出画布上）
        var margin = self.style.margin * globalScale;
        var availableW = exportWidth - margin * 2;
        var availableH = exportHeight - margin * 2;
        
        var cellWidth = (cell.w / 100) * availableW;
        var cellHeight = (cell.h / 100) * availableH;
        var cellX = margin + (cell.x / 100) * availableW;
        var cellY = margin + (cell.y / 100) * availableH;
        
        // 计算基础缩放（填满格子）
        var baseScaleX = cellWidth / imgWidth;
        var baseScaleY = cellHeight / imgHeight;
        var baseScale = Math.max(baseScaleX, baseScaleY);
        
        // 最终缩放 = 基础缩放 * 用户缩放
        var finalScale = baseScale * imageScale;
        
        // 计算绘制尺寸和位置
        var drawWidth = imgWidth * finalScale;
        var drawHeight = imgHeight * finalScale;
        var drawX = cellX + (cellWidth - drawWidth) / 2;
        var drawY = cellY + (cellHeight - drawHeight) / 2;
        
        // 应用偏移（偏移在用户空间，需要乘以基础缩放）
        drawX += offset.x * baseScale;
        drawY += offset.y * baseScale;
        
        // 应用全局缩放（因为导出尺寸可能和显示尺寸不同）
        // 注意：drawX/drawY/drawWidth/drawHeight 已经是按导出尺寸计算的，不需要再乘 globalScale
        
        ctx.save();
        ctx.beginPath();
        var radius = self.style.borderRadius * globalScale;
        ctx.moveTo(cellX + radius, cellY);
        ctx.lineTo(cellX + cellWidth - radius, cellY);
        ctx.quadraticCurveTo(cellX + cellWidth, cellY, cellX + cellWidth, cellY + radius);
        ctx.lineTo(cellX + cellWidth, cellY + cellHeight - radius);
        ctx.quadraticCurveTo(cellX + cellWidth, cellY + cellHeight, cellX + cellWidth - radius, cellY + cellHeight);
        ctx.lineTo(cellX + radius, cellY + cellHeight);
        ctx.quadraticCurveTo(cellX, cellY + cellHeight, cellX, cellY + cellHeight - radius);
        ctx.lineTo(cellX, cellY + radius);
        ctx.quadraticCurveTo(cellX, cellY, cellX + radius, cellY);
        ctx.closePath();
        ctx.clip();
        
        ctx.drawImage(img, 0, 0, imgWidth, imgHeight, drawX, drawY, drawWidth, drawHeight);
        ctx.restore();
      }
      
      try {
        var result = canvas.toDataURL(self.exportFormat, self.exportQuality);
        return result;
      } catch (error) {
        console.error('生成拼图失败:', error);
        alert('生成拼图失败，请重试');
        return null;
      }
    },
    
    closeModal: function() {
      this.$emit('close');
    },
  },
};
</script>

<style scoped>
* {
  box-sizing: border-box;
}

.photo-collage {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  background: #f0f2f5;
  min-height: 80vh;
}

.editor-container {
  display: flex;
  gap: 20px;
  padding: 20px;
  min-height: calc(100vh - 120px);
}

.layout-sidebar {
  width: 320px;
  background: white;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  overflow-y: auto;
  max-height: calc(100vh - 160px);
}

.layout-header h3 {
  margin: 0 0 4px 0;
  color: #1a1a2e;
}

.layout-filter {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 20px;
}

.filter-btn {
  padding: 4px 12px;
  border: 1px solid #ddd;
  border-radius: 20px;
  background: white;
  cursor: pointer;
  font-size: 13px;
  transition: all 0.2s;
}

.filter-btn.active {
  background: #007DFF;
  color: white;
  border-color: #007DFF;
}

.layout-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

.layout-card {
  border: 2px solid #e8e8e8;
  border-radius: 10px;
  padding: 10px;
  cursor: pointer;
  transition: all 0.2s;
  text-align: center;
}

.layout-card:hover {
  border-color: #007DFF;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(76,175,80,0.15);
}

.layout-card.active {
  border-color: #007DFF;
}

.layout-preview {
  width: 100%;
  height: 80px;
  margin-bottom: 8px;
}

.preview-canvas {
  width: 100%;
  height: 100%;
  border-radius: 6px;
  background: #f0f0f0;
}

.layout-name {
  font-size: 12px;
  font-weight: 500;
  color: #333;
}

.layout-count {
  font-size: 10px;
  color: #999;
  margin-top: 2px;
}

.canvas-area {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  min-height: 600px;
}

.batch-upload-bar {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  width: 100%;
  border-bottom: 1px solid #e8e8e8;
  padding-bottom: 10px;
  margin-bottom: 10px;
}

.batch-upload-bar button {
  margin-left: 10px;
}

.btn-batch-upload {
  background: #fff;
  color: #333;
  border: 1px solid #e8e8e8;
  padding: 8px 20px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

.btn-batch-upload:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 10px rgba(0,0,0,0.15);
}

.canvas-wrapper {
  position: relative;
  background: #fff;
  transition: all 0.2s;
}

.canvas-bg {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 0;
}

.layout-cell {
  position: absolute;
  z-index: 1;
  transition: all 0.2s;
  border: 2px solid transparent;
  overflow: hidden;
}

.layout-cell:hover {
  border-color: #007DFF;
  z-index: 2;
}

.cell-image-wrapper {
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
  cursor: grab;
  display: flex;
  align-items: center;
  justify-content: center;
}

.cell-image-wrapper:active {
  cursor: grabbing;
}

.cell-image {
  display: block;
  position: relative;
  transition: transform 0.2s ease;
}

.cell-placeholder {
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  cursor: pointer;
}

.upload-icon-small {
  font-size: 32px;
  margin-bottom: 8px;
}

.upload-text-small {
  font-size: 12px;
  color: #666;
}

.cell-index {
  position: absolute;
  top: 8px;
  left: 8px;
  background: rgba(0,0,0,0.6);
  color: white;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
  z-index: 3;
}

.cell-actions {
  position: absolute;
  bottom: 8px;
  right: 8px;
  display: flex;
  gap: 8px;
  z-index: 3;
  opacity: 0;
  transition: opacity 0.2s;
}

.layout-cell:hover .cell-actions {
  opacity: 1;
}

.cell-action-btn {
  background: rgba(0,0,0,0.7);
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  cursor: pointer;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.cell-action-btn:hover {
  background: #007DFF;
  transform: scale(1.1);
}

.no-layout-tip {
  text-align: center;
  padding: 40px;
  background: white;
  border-radius: 12px;
  color: #f44336;
}

.settings-sidebar {
  width: 280px;
  background: white;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  overflow-y: auto;
  max-height: calc(100vh - 160px);
}

.settings-section {
  margin-bottom: 24px;
  padding-bottom: 20px;
  border-bottom: 1px solid #eee;
}

.settings-section:last-child {
  border-bottom: none;
}

.settings-section h3, .export-controls h3 {
  margin: 0 0 16px 0;
  font-size: 16px;
  color: #333;
}

.setting-group {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
}

.setting-group label {
  width: 70px;
  font-size: 13px;
  color: #666;
}

.setting-group input[type="range"] {
  flex: 1;
}

.setting-group input[type="color"] {
  width: 50px;
  height: 32px;
  border: 1px solid #ddd;
  border-radius: 6px;
  cursor: pointer;
}

.setting-group span {
  width: 40px;
  font-size: 12px;
  color: #999;
}

.ratio-buttons {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.ratio-buttons button {
  padding: 4px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  background: white;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s;
}

.ratio-buttons button.active {
  background: #007DFF;
  color: white;
  border-color: #007DFF;
}

.status-info {
  font-size: 13px;
  color: #666;
  line-height: 1.8;
}

.hint-info {
  color: #007DFF;
  margin-top: 8px;
}

.success-info {
  color: #007DFF;
  margin-top: 8px;
}

.btn-clear-all {
  padding: 8px 20px;
  background: #ff9800;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
}

.btn-clear-all:hover {
  background: #e68900;
}

.export-controls {
  display: block;
}

.export-controls .export-controls-buttons {
  display: flex;
  gap: 10px;
  margin-top: 15px;
}

.export-controls-buttons button {
  flex: 1;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-export {
  background: #007DFF;
  color: white;
  border: none;
}

.btn-export:hover:not(:disabled) {
  background: #0066cc;
}

.btn-use {
  background: #007DFF;
  color: white;
  border: none;
}

.btn-use:hover:not(:disabled) {
  background: #0066cc;
}

.btn-export:disabled,
.btn-use:disabled {
  background: #ccc;
  color: #666;
  cursor: not-allowed;
}

.tab-group {
  margin-bottom: 12px;
}

.tab-header {
  display: flex;
  border-radius: 8px;
  padding: 3px;
  gap: 8px;
  align-items: center;
}

.tab-header span {
  font-size: 12px;
  color: #666;
}

.tab-btn {
  flex: 1;
  padding: 6px 8px;
  border: 1px solid #ddd;
  background: white;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s;
  color: #666;
  white-space: pre-line;
  line-height: 1.3;
}

.tab-btn:hover {
  border-color: #007DFF;
  color: #007DFF;
}

.tab-btn.active {
  background: #007DFF;
  color: white;
  border-color: #007DFF;
}

.zoom-controls {
  position: absolute;
  bottom: 50px;
  right: 8px;
  display: flex;
  gap: 4px;
  z-index: 3;
  opacity: 0;
  transition: opacity 0.2s;
  background: rgba(0, 0, 0, 0.6);
  border-radius: 20px;
  padding: 4px 8px;
  align-items: center;
}

.layout-cell:hover .zoom-controls {
  opacity: 1;
}

.zoom-btn {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.9);
  cursor: pointer;
  font-size: 16px;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  color: #333;
}

.zoom-btn:hover {
  background: #007DFF;
  color: white;
  transform: scale(1.05);
}

.zoom-scale {
  font-size: 11px;
  color: white;
  min-width: 40px;
  text-align: center;
  font-weight: 500;
}

.drag-preview {
  transition: none;
  pointer-events: none;
  filter: drop-shadow(0 4px 12px rgba(0,0,0,0.3));
}

@media (max-width: 1200px) {
  .editor-container {
    flex-direction: column;
  }
  
  .layout-sidebar,
  .settings-sidebar {
    width: 100%;
    max-height: 300px;
  }
  
  .canvas-area {
    min-height: 500px;
  }
}
</style>